var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
};
var _SsnFieldManager_instances, _SsnFieldManager_unFormatValue, _SsnFieldManager_getValidityState, _SsnFieldManager_clearValue, _SsnFieldManager_dispatchValidityEvent, _SsnFieldManager_handleInputDelete, _SsnFieldManager_handleInputInsertText, _SsnFieldManager_onBlur, _SsnFieldManager_onKeydown, _SsnFieldManager_onFieldMutation, _SsnFieldManager_onFormData, _SsnFieldManager_onPaste, _SsnFieldManager_onInput, _SsnFieldManager_onInputDisabledMutation, _SsnFieldManager_onToggleButtonClick, _SsnFieldManager_setToggleButtonConfiguration, _SsnFieldManager_updateInputValueForUseMaskState;
import { ComponentManagerBase } from '@cof/fs-component-manager-base';
import { FsIcon } from '@cof/fs-icon-wc';
import { fs16Show, fs16ShowHide, fs24Show, fs24ShowHide } from '@cof/fs-icons';
import { FsTooltip } from '@cof/fs-tooltip-wc';
import { privateFsDefineComponent } from '@cof/safe-custom-element-define';
import { formatSSN, maskFirstFiveNumbers, matchValueInString, modifySelectedRangeOfSsnValue, spliceCharactersIntoString, ssnMaskPattern, ssnRegex } from './ssn-utils.js';
import { SsnFieldInputEvent } from './SsnFieldEvent.js';
import { ssnFieldManagerConfigDefaults } from './types.js';
/** Define a private version of the FsIcon component and register the necessary icons */
const privateIconTagName = privateFsDefineComponent.define(FsIcon).safeTagName;
FsIcon.add([fs16Show, fs16ShowHide, fs24Show, fs24ShowHide]);
/** Define a private version of the FsTooltip component */
const privateTooltipTagName = privateFsDefineComponent.define(FsTooltip).safeTagName;
export class SsnFieldManager extends ComponentManagerBase {
    get toggleMaskBtn() {
        return this._toggleButton;
    }
    // TODO: add this to other managers
    get preferredValue() {
        return this._config.useMaskedValue ? this.formattedValue : this.unFormattedValue;
    }
    /**
     * @returns {string} formatted virtual ssn field value ex: 333-22-4444
     */
    get formattedValue() {
        return formatSSN(this._realValue, this._config).formattedValue;
    }
    /**
     * @returns {string} un-formatted virtual ssn field value ex: 333224444
     */
    get unFormattedValue() {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_unFormatValue).call(this, this._realValue);
    }
    /**
     * @returns {string} masked and un-formatted virtual ssn field value ex: •••••4444
     */
    get maskedButUnformatted() {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return maskFirstFiveNumbers(this._realValue, this._config.maskedCharacter).replaceAll(this._config.separator, '');
    }
    /**
     * @returns {string} masked and formatted virtual ssn field value ex: •••-••-4444
     */
    get maskedAndFormatted() {
        return maskFirstFiveNumbers(formatSSN(this._realValue, this._config).formattedValue);
    }
    constructor(formField, config = {}) {
        super();
        _SsnFieldManager_instances.add(this);
        this._previousValue = ''; // the previous input.value | target.value
        this._realValue = ''; // the realized value unformatted and unmasked
        this._isInputDisplayValueMasked = true;
        this._memoizedInputProperties = {};
        /** Apply formatting to the field on blur if the SSN matches the valid format */
        _SsnFieldManager_onBlur.set(this, () => {
            this.setValue(this.unFormattedValue);
        });
        /** Generic handle keydown method, only allows numeric keys or the separator */
        _SsnFieldManager_onKeydown.set(this, (event) => {
            /** If a key is held down, do nothing */
            if (event.repeat) {
                return;
            }
            const { key, target } = event;
            this._previousValue = target.value;
            this._selectionStart = target.selectionStart;
            this._selectionEnd = target.selectionEnd;
            const ignoredKeys = [
                'Backspace',
                'Delete',
                'Tab',
                'Enter',
                'Escape',
                'Shift',
                'ArrowLeft',
                'ArrowRight',
                'ShiftLeft',
                'ShiftRight'
            ];
            const insertedCharacterIsANumber = !isNaN(+key);
            const newValueIsLongerThanAllowedMax = target.maxLength !== -1 &&
                insertedCharacterIsANumber &&
                target.value?.length + 1 > target.maxLength &&
                target.selectionStart - target.selectionEnd === 0;
            if (ignoredKeys.includes(key) || event.metaKey || newValueIsLongerThanAllowedMax) {
                return;
            }
            if (!insertedCharacterIsANumber && key !== this._config.separator) {
                event.preventDefault();
                return;
            }
            this._realValue = modifySelectedRangeOfSsnValue(this._selectionStart, this._selectionEnd, target.value, this._realValue, key, 'insert');
        });
        /** Toggle the icon depending on the current mask and compact state of the text field */
        _SsnFieldManager_onFieldMutation.set(this, (mutations) => {
            mutations.forEach(() => __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_setToggleButtonConfiguration).call(this));
        });
        /** Triggers when the form is submitted or the value read to keep form semantics accurate */
        _SsnFieldManager_onFormData.set(this, ({ formData }) => {
            const name = this._input.name;
            if (name) {
                formData.set(name, this.unFormattedValue);
            }
        });
        _SsnFieldManager_onPaste.set(this, (event) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const clipboardText = event.clipboardData.getData('text').replaceAll(' ', '');
            if (isNaN(+clipboardText)) {
                return;
            }
            let spliceStartIndex = this._selectionStart;
            let spliceDeleteCount = this._selectionEnd - this._selectionStart;
            const selectionRangePosition = clipboardText.length;
            const hasSeparatorsInSelectionRangeValue = matchValueInString(event.target['value'], this._config.separator, this._selectionStart, this._selectionEnd);
            if (hasSeparatorsInSelectionRangeValue) {
                spliceDeleteCount = this._selectionEnd - this._selectionStart - hasSeparatorsInSelectionRangeValue.length;
            }
            if (this._selectionStart >= 4) {
                spliceStartIndex = this._selectionStart - 1;
            }
            if (this._selectionStart >= 7) {
                spliceStartIndex = this._selectionStart - 2;
            }
            this._realValue = spliceCharactersIntoString(this._realValue, clipboardText, spliceStartIndex, spliceDeleteCount);
            // using a setTimeout to overwrite the default pasted value with custom value.
            // also so that all the original events that bubble up from on paste are still triggers
            // if event.preventDefault is used change, input will not be triggered
            setTimeout(() => {
                this._input.value = this._isInputDisplayValueMasked ? maskFirstFiveNumbers(this._realValue) : this._realValue;
                this._input.setSelectionRange(selectionRangePosition + spliceStartIndex, selectionRangePosition + spliceStartIndex);
                __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_dispatchValidityEvent).call(this);
            }, 0);
        });
        /**
         * When an input event occurs forward the event to the appropriate callback
         * based on the event's inputType property.
         */
        _SsnFieldManager_onInput.set(this, (event) => {
            switch (event.inputType) {
                case 'deleteContentBackward':
                case 'deleteWordBackward': {
                    __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_handleInputDelete).call(this);
                    break;
                }
                case 'deleteSoftLineBackward': {
                    __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_clearValue).call(this);
                    break;
                }
                case 'insertText': {
                    __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_handleInputInsertText).call(this, event);
                    break;
                }
                default: {
                    break;
                }
            }
        });
        /**
         * Callback for the input disabled mutation observer. When the observer sees a change
         * to the input's disabled state we want to modify our tooltip and button state
         */
        _SsnFieldManager_onInputDisabledMutation.set(this, (mutations) => {
            mutations.forEach(() => {
                const isDisabled = this._input.disabled;
                this._toggleButton.disabled = isDisabled;
                if (isDisabled) {
                    this._tooltip.remove();
                    this._input.after(this._toggleButton);
                }
                else {
                    this._tooltip.append(this._toggleButton);
                    this._input.after(this._tooltip);
                }
            });
        });
        /** When the icon button is clicked we need to toggle the button and field configuration */
        _SsnFieldManager_onToggleButtonClick.set(this, () => {
            this._isInputDisplayValueMasked = !this._isInputDisplayValueMasked;
            __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_setToggleButtonConfiguration).call(this);
            __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_updateInputValueForUseMaskState).call(this);
        });
        const input = formField.querySelector('input');
        /** Throw an error if there is no input present */
        // eslint-disable-next-line no-prototype-builtins
        if (!input || !HTMLInputElement.prototype.isPrototypeOf(input)) {
            throw new TypeError('An instance of the HTMLInputElement is required when creating an instance of the SsnFieldManager.');
        }
        /** Throw an error if the input doesn't have an id to associate with the toggle button */
        if (!input.id) {
            throw new Error('The input with the SsnFieldManager is missing an id attribute.');
        }
        this._config = {
            ...ssnFieldManagerConfigDefaults,
            ...config
        };
        this._field = formField;
        this._input = input;
        /**
         * Create the toggle button and tooltip and configure them to work
         * with the current SSN field configuration
         */
        this._toggleButton = document.createElement(privateIconTagName);
        this._tooltip = document.createElement(privateTooltipTagName);
        this._tooltip.append(this._toggleButton);
        this._tooltip.tooltipContent = this._config.showMessage;
        __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_setToggleButtonConfiguration).call(this);
        this._toggleButton.interactive = true;
        this._toggleButton.setAttribute('aria-controls', input.id);
        /** Create the mutation observer objects and prepare them for connection */
        this._fieldObserver = new MutationObserver(__classPrivateFieldGet(this, _SsnFieldManager_onFieldMutation, "f"));
        this._inputDisabledObserver = new MutationObserver(__classPrivateFieldGet(this, _SsnFieldManager_onInputDisabledMutation, "f"));
    }
    /** Call `connect` when you want the SSN field manager to operate on the given text field */
    connect() {
        super.connect();
        /** Add event listeners */
        this._input.addEventListener('blur', __classPrivateFieldGet(this, _SsnFieldManager_onBlur, "f"), this.abortableEventListenerOptions);
        this._input.addEventListener('keydown', __classPrivateFieldGet(this, _SsnFieldManager_onKeydown, "f"), this.abortableEventListenerOptions);
        this._input.addEventListener('input', __classPrivateFieldGet(this, _SsnFieldManager_onInput, "f"), this.abortableEventListenerOptions);
        this._input.addEventListener('paste', __classPrivateFieldGet(this, _SsnFieldManager_onPaste, "f"), this.abortableEventListenerOptions);
        this._toggleButton.addEventListener('click', __classPrivateFieldGet(this, _SsnFieldManager_onToggleButtonClick, "f"), this.abortableEventListenerOptions);
        /** Configure the provided input */
        this._memoizedInputProperties = {
            autocomplete: this._input.autocomplete,
            inputMode: this._input.inputMode
        };
        this._input.autocomplete = 'off';
        this._input.inputMode = 'decimal';
        /**
         * Since we will be modifying/masking the input's value we need to provide a way for native APIs
         * to access the actual value of the SSN field.
         *
         * TODO: Figure out how this will play with Solid and React's preferred ways of doing things
         */
        if (this._input.form) {
            this._input.form.addEventListener('formdata', __classPrivateFieldGet(this, _SsnFieldManager_onFormData, "f"));
        }
        /** Append the tooltip and toggle button combo after the input element */
        this._input.after(this._tooltip);
        /**
         * Connect the mutation observers. Since these don't accept AbortSignals, we will need
         * to manually disconnect these in the manager's disconnect method
         */
        this._inputDisabledObserver.observe(this._input, {
            attributeFilter: ['disabled']
        });
        this._fieldObserver.observe(this._field, {
            attributeFilter: ['class']
        });
    }
    /** Should be called when the SSN field manager is no longer needed */
    disconnect() {
        /** Will remove event listeners from connect */
        super.disconnect();
        /** Clean up mutation observers */
        this._inputDisabledObserver.disconnect();
        this._fieldObserver.disconnect();
        /** Remove inserted nodes */
        this._tooltip.remove();
        this._toggleButton.remove();
        /** Revert the input to its original state */
        this._input.autocomplete = this._memoizedInputProperties.autocomplete;
        this._input.inputMode = this._memoizedInputProperties.inputMode;
    }
    setValue(value) {
        const input = this._input;
        const isValidSsn = this.unFormattedValue.match(ssnMaskPattern);
        this._realValue = value;
        if (this._isInputDisplayValueMasked) {
            input.value = isValidSsn ? this.maskedAndFormatted : this.maskedButUnformatted;
        }
        else {
            input.value = isValidSsn ? this.formattedValue : this.unFormattedValue;
        }
    }
    /** Update the config and dispatch any events that need to be sent to consumers */
    updateConfig(newConfig) {
        this._config = {
            ...this._config,
            ...newConfig
        };
        __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_setToggleButtonConfiguration).call(this);
        this.setValue(this.unFormattedValue);
        __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_dispatchValidityEvent).call(this);
        return this._config;
    }
}
_SsnFieldManager_onBlur = new WeakMap(), _SsnFieldManager_onKeydown = new WeakMap(), _SsnFieldManager_onFieldMutation = new WeakMap(), _SsnFieldManager_onFormData = new WeakMap(), _SsnFieldManager_onPaste = new WeakMap(), _SsnFieldManager_onInput = new WeakMap(), _SsnFieldManager_onInputDisabledMutation = new WeakMap(), _SsnFieldManager_onToggleButtonClick = new WeakMap(), _SsnFieldManager_instances = new WeakSet(), _SsnFieldManager_unFormatValue = function _SsnFieldManager_unFormatValue(value) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return value.replaceAll(this._config.separator, '');
}, _SsnFieldManager_getValidityState = function _SsnFieldManager_getValidityState() {
    const baseValidity = this._input.validity;
    const customValidity = {
        badInput: baseValidity.badInput,
        customError: baseValidity.customError,
        patternMismatch: baseValidity.patternMismatch,
        rangeOverflow: baseValidity.rangeOverflow,
        rangeUnderflow: baseValidity.rangeUnderflow,
        stepMismatch: baseValidity.stepMismatch,
        tooLong: baseValidity.tooLong,
        tooShort: baseValidity.tooShort,
        typeMismatch: baseValidity.typeMismatch,
        valueMissing: baseValidity.valueMissing
    };
    let valid = baseValidity.valid;
    if (!this._config.dangerouslyDisablePatternValidation) {
        const validPattern = !!ssnRegex.exec(this.formattedValue);
        customValidity.patternMismatch = !validPattern;
        valid = validPattern;
    }
    if (this.unFormattedValue.length > 9) {
        customValidity.tooLong = true;
        valid = false;
    }
    else if (this.unFormattedValue.length < 9) {
        customValidity.tooShort = true;
        valid = false;
    }
    return { ...customValidity, valid };
}, _SsnFieldManager_clearValue = function _SsnFieldManager_clearValue() {
    this._realValue = '';
    this._input.value = '';
    __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_dispatchValidityEvent).call(this);
}, _SsnFieldManager_dispatchValidityEvent = function _SsnFieldManager_dispatchValidityEvent() {
    const validity = __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_getValidityState).call(this);
    const event = new SsnFieldInputEvent(validity, this.formattedValue, this.unFormattedValue, this._input);
    this.dispatchEvent(event);
}, _SsnFieldManager_handleInputDelete = function _SsnFieldManager_handleInputDelete() {
    this._realValue = modifySelectedRangeOfSsnValue(this._selectionStart, this._selectionEnd, this._previousValue, this._realValue);
    __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_dispatchValidityEvent).call(this);
}, _SsnFieldManager_handleInputInsertText = function _SsnFieldManager_handleInputInsertText(event) {
    /**
     * If the manager is using the mask, we want need to overwrite the input value
     * and manages the user's selection location
     */
    if (this._isInputDisplayValueMasked) {
        const { target } = event;
        let selectionStart = target.selectionStart;
        const hasSeparatorsInUnselectedRange = matchValueInString(target.value, this._config.separator, 0, target.selectionStart - 1 // minus 1 because 1 character got added?
        );
        if (hasSeparatorsInUnselectedRange) {
            selectionStart = selectionStart - hasSeparatorsInUnselectedRange.length;
        }
        target.value = maskFirstFiveNumbers(this._realValue);
        target.setSelectionRange(selectionStart, selectionStart);
    }
    __classPrivateFieldGet(this, _SsnFieldManager_instances, "m", _SsnFieldManager_dispatchValidityEvent).call(this);
}, _SsnFieldManager_setToggleButtonConfiguration = function _SsnFieldManager_setToggleButtonConfiguration() {
    const isCompact = this._field.classList.contains(this._config.compactClass);
    const toggleButton = this._toggleButton;
    const useMask = this._isInputDisplayValueMasked;
    let newIcon;
    if (useMask && isCompact) {
        newIcon = fs16Show;
    }
    else if (useMask && !isCompact) {
        newIcon = fs24Show;
    }
    else if (!useMask && isCompact) {
        newIcon = fs16ShowHide;
    }
    else if (!useMask && !isCompact) {
        newIcon = fs24ShowHide;
    }
    const newMessage = this._config[useMask ? 'showMessage' : 'hideMessage'];
    this._tooltip.tooltipContent = newMessage;
    if (this._toggleButton.shadowRoot) {
        this._toggleButton.shadowRoot.querySelector('button')?.setAttribute('aria-pressed', String(!useMask));
    }
    this._toggleButton.description = newMessage;
    toggleButton.icon = newIcon[0];
}, _SsnFieldManager_updateInputValueForUseMaskState = function _SsnFieldManager_updateInputValueForUseMaskState() {
    const matchesExpectedPattern = this.unFormattedValue.match(ssnMaskPattern);
    const input = this._input;
    if (matchesExpectedPattern) {
        input.value = this._isInputDisplayValueMasked ? this.maskedAndFormatted : this.formattedValue;
    }
    else {
        input.value = this._isInputDisplayValueMasked ? this.maskedButUnformatted : this.unFormattedValue;
    }
};
//# sourceMappingURL=ssn-field-manager.js.map